import { z } from 'zod';
import { compareTwoStrings } from 'string-similarity';

// Validation schema for FAQ entries
export const faqEntrySchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  category: z.string(),
  keywords: z.array(z.string()).optional(),
  variations: z.array(z.string()).optional(),
  links: z.array(z.object({
    text: z.string(),
    url: z.string()
  })).optional(),
  callToAction: z.object({
    text: z.string(),
    number: z.string()
  }).optional()
});

export type FAQEntry = z.infer<typeof faqEntrySchema>;

interface MatchResult {
  entry: FAQEntry;
  score: number;
}

// Common word replacements for normalization
const wordReplacements: Record<string, string> = {
  "dont": "do not",
  "cant": "cannot",
  "wont": "will not",
  "im": "i am",
  "ill": "i will",
  "youre": "you are",
  "theyre": "they are",
  "couldnt": "could not",
  "wouldnt": "would not",
  "shouldnt": "should not",
  "hasnt": "has not",
  "havent": "have not",
  "hadnt": "had not",
  "isnt": "is not",
  "arent": "are not",
  "wasnt": "was not",
  "werent": "were not"
};

// Normalize text for comparison
export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    // Remove punctuation
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
    // Replace multiple spaces with single space
    .replace(/\s+/g, " ")
    // Replace common contractions
    .split(" ")
    .map(word => wordReplacements[word] || word)
    .join(" ")
    .trim();
}

// Get key phrases from text
export function getKeyPhrases(text: string): string[] {
  const normalized = normalizeText(text);
  const words = normalized.split(" ");
  const phrases: string[] = [];

  // Single words (excluding stop words)
  const stopWords = new Set(["the", "a", "an", "and", "or", "but", "in", "on", "at", "to", "for", "of", "with", "by"]);
  words.forEach(word => {
    if (!stopWords.has(word) && word.length > 2) {
      phrases.push(word);
    }
  });

  // Bi-grams
  for (let i = 0; i < words.length - 1; i++) {
    phrases.push(`${words[i]} ${words[i + 1]}`);
  }

  // Tri-grams
  for (let i = 0; i < words.length - 2; i++) {
    phrases.push(`${words[i]} ${words[i + 1]} ${words[i + 2]}`);
  }

  return [...new Set(phrases)];
}

// Find best matching FAQ entry
export function findBestMatch(
  query: string,
  faqEntries: FAQEntry[],
  threshold: number = 0.85
): MatchResult | null {
  const normalizedQuery = normalizeText(query);
  const queryPhrases = getKeyPhrases(query);
  
  const matches = faqEntries.map(entry => {
    // Calculate similarity scores
    const questionScore = compareTwoStrings(
      normalizedQuery,
      normalizeText(entry.question)
    );

    // Check variations if available
    const variationScores = entry.variations?.map(variation =>
      compareTwoStrings(normalizedQuery, normalizeText(variation))
    ) || [];

    // Check keyword matches
    const keywordMatches = entry.keywords?.filter(keyword =>
      queryPhrases.some(phrase => 
        compareTwoStrings(normalizeText(phrase), normalizeText(keyword)) > 0.8
      )
    ).length || 0;

    // Calculate final score
    const maxVariationScore = Math.max(0, ...variationScores);
    const keywordScore = keywordMatches / (entry.keywords?.length || 1);
    
    const score = Math.max(
      questionScore,
      maxVariationScore,
      keywordScore
    );

    return { entry, score };
  });

  // Sort by score and get the best match
  const bestMatch = matches.sort((a, b) => b.score - a.score)[0];

  return bestMatch.score >= threshold ? bestMatch : null;
}

// Get related questions based on category and keywords
export function getRelatedQuestions(
  currentQuestion: FAQEntry,
  faqEntries: FAQEntry[],
  limit: number = 3
): FAQEntry[] {
  return faqEntries
    .filter(entry => 
      entry.id !== currentQuestion.id && (
        entry.category === currentQuestion.category ||
        entry.keywords?.some(k1 => 
          currentQuestion.keywords?.some(k2 => 
            compareTwoStrings(k1, k2) > 0.8
          )
        )
      )
    )
    .slice(0, limit);
}
